<template lang="pug">
.sticker-card
  .sticker-card__header(@click="expanded = !expanded")
    .sticker-card__thumb
      img(v-if="previewUrl" :src="previewUrl" :alt="draft.name")
      fai(v-else type="fal" icon="image").size-18
    .sticker-card__info
      p.sticker-card__name {{ draft.name || `Sticker ${index + 1}` }}
      p.sticker-card__meta
        | {{ draft.codes.length }} unlock code{{ draft.codes.length !== 1 ? 's' : '' }}
        span.sticker-card__tagged(v-if="draft.mode === 'existing' && draft.stickerId")
          fai(type="fal" icon="tag").size-14
          | tagged
    .sticker-card__actions(@click.stop)
      span.sticker-card__badge(
        :class="draft.mode === 'new' ? 'sticker-card__badge--new' : 'sticker-card__badge--tagged'"
      ) {{ draft.mode === 'new' ? 'New' : 'Tagged' }}
      button.sticker-card__icon-btn(type="button" title="Remove" @click="$emit('remove')")
        fai(type="fal" icon="trash").size-14
      span.sticker-card__chevron(
        :class="{ 'is-open': expanded }"
        @click="expanded = !expanded"
      )
        fai(type="fal" icon="chevron-down").size-14

  .sticker-card__body(v-if="expanded")
    div
      p.caps-label Sticker source
      .source-toggle
        button.source-toggle__btn(
          type="button"
          :class="{ 'is-active': draft.mode === 'new' }"
          @click="setMode('new')"
        ) ✦ Create new
        button.source-toggle__btn(
          type="button"
          :class="{ 'is-active': draft.mode === 'existing' }"
          @click="setMode('existing')"
        ) ◈ Tag existing

    .d-flex.flex-column.gap-3(v-if="draft.mode === 'new'")
      div
        label.field-label(:for="`draft-name-${draft.key}`")
          | Sticker name
          span.field-label__req  *
        input.field-input(
          :id="`draft-name-${draft.key}`"
          :value="draft.name"
          placeholder="e.g. Green Thumb"
          @input="patch({ name: inputValue($event) })"
        )
      div
        span.field-label Sticker image
        label.image-upload
          .image-upload__box
            img(v-if="draft.imageUrl" :src="draft.imageUrl" :alt="draft.name")
            fai(v-else type="fal" icon="plus").size-16
          div
            p.image-upload__title {{ uploading ? 'Uploading…' : 'Upload image' }}
            p.image-upload__hint PNG or JPG
          input.d-none(
            type="file"
            accept="image/png,image/jpeg"
            :disabled="uploading"
            @change="onImageSelected"
          )
    div(v-else)
      label.field-label
        | Select existing sticker
        span.field-label__req  *
      el-select.sticker-select(
        :model-value="draft.stickerId || null"
        filterable
        remote
        :remote-method="searchStickers"
        :loading="loadingStickers"
        placeholder="— Choose a sticker —"
        no-data-text="No stickers found"
        @update:model-value="selectSticker"
      )
        el-option(
          v-for="s in stickerOptions"
          :key="s.id"
          :label="s.title"
          :value="s.id"
        )
      p.image-upload__hint.mt-1(v-if="loadedOnce && !hasAnySticker")
        | This organization has no approved stickers yet. Use “Create new” instead.

    .clue
      .clue__head
        span.caps-label Clue / Hint (Optional)
        button.clue__link(type="button" @click="toggleClue")
          | {{ showClue ? 'Remove' : '+ Add clue' }}
      textarea.field-input.field-input--textarea(
        v-if="showClue"
        :value="draft.clue"
        rows="2"
        maxlength="200"
        placeholder='Hint shown to collectors, e.g. "Find us at the Saturday farmers market booth"'
        @input="patch({ clue: inputValue($event) })"
      )

    .codes
      .codes__head
        p.caps-label.codes__title
          fai(type="fal" icon="key").size-14
          | Unlock codes
        el-tooltip(
          :disabled="!requiresCodeBlockedReason"
          :content="requiresCodeBlockedReason"
          placement="top"
        )
          label.codes__required
            span Required
            button.codes__toggle(
              type="button"
              :class="{ 'is-on': draft.requiresCode }"
              :disabled="Boolean(requiresCodeBlockedReason)"
              @click="patch({ requiresCode: !draft.requiresCode })"
            )
      .codes__note(v-if="!draft.requiresCode")
        span ⚡
        span No code required — this sticker is automatically available to all collectors.

      .codes__list(v-if="draft.codes.length")
        .code-row(v-for="(code, i) in draft.codes" :key="code.code")
          span.code-row__code {{ code.code }}
          span.code-row__uses 0 uses
          .code-row__max
            input.code-row__max-input(
              type="number"
              min="1"
              placeholder="∞"
              :value="code.maxUses ?? ''"
              @change="setCodeMaxUses(i, $event)"
            )
            span.code-row__max-label max
          button.code-row__remove(type="button" title="Remove code" @click="removeCode(i)")
            fai(type="fal" icon="times").size-14

      .codes__add(v-if="draft.requiresCode")
        input.codes__add-input(
          v-model="newCode"
          :placeholder="codeInputPlaceholder"
          @keydown.enter="addCode"
        )
        button.codes__add-btn(type="button" :disabled="!canAddCode" @click="addCode")
          fai(type="fal" icon="plus").size-16
          | Add
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { ElMessage } from 'element-plus';
import globalShared from '@/shared';
import fileUpload from '@/shared/upload';
import { findApprovedOrgStickers } from '@/graphs/gql';
import type { StickerCollectionCodeFormat } from '@/data/stickerCollection.types';
import {
  codeInputPlaceholder as placeholderForFormat,
  codeRequirementBlockedReason,
  generateCollectionCode,
  parseMaxUses,
  type DraftSticker,
  type DraftStickerMode,
} from '../shared';

interface StickerOption {
  id: string;
  title: string;
  url?: string;
  thumbnailUrl?: string;
  cost?: number;
  campaignType?: string;
  entityId?: string;
}

export default defineComponent({
  name: 'CollectionStickerDraftCard',
  props: {
    draft: { type: Object as () => DraftSticker, required: true },
    index: { type: Number, required: true },
    orgId: { type: String, required: true },
    excludeStickerIds: { type: Array as () => string[], default: () => [] },
    collectionOwnerId: { type: String, default: '' },
    codeFormat: { type: String as () => StickerCollectionCodeFormat, default: 'ALPHANUMERIC' },
    /** Collection default for new codes; null = unlimited. */
    defaultCodeMaxUses: { type: Number as () => number | null, default: null },
  },
  emits: ['update:draft', 'remove'],
  setup(props, { emit }) {
    const { makeGraphRequest, getAuthToken } = globalShared();
    const { uploadStatus, beforeUpload, handleUploadRequest } = fileUpload('stickers', 'Sticker');
    const expanded = ref(true);
    const showClue = ref(Boolean(props.draft.clue));
    const newCode = ref('');
    const uploading = ref(false);
    const loadingStickers = ref(false);
    const loadedOnce = ref(false);
    const stickers = ref<StickerOption[]>([]);
    const hasAnySticker = ref(false);
    // Keeps the selected sticker's preview after the options list is re-searched.
    const selected = ref<StickerOption | null>(null);

    const previewUrl = computed(() => {
      if (props.draft.mode === 'new') return props.draft.thumbnailUrl || props.draft.imageUrl;
      return selected.value?.url || selected.value?.thumbnailUrl || '';
    });

    const stickerOptions = computed(() => {
      const excluded = new Set(
        props.excludeStickerIds.filter((id) => id !== props.draft.stickerId),
      );
      const list = stickers.value.filter((s) => !excluded.has(s.id));
      if (selected.value && !list.some((s) => s.id === selected.value?.id)) {
        list.unshift(selected.value);
      }
      return list;
    });

    // New stickers are created free and owned by the org, so only tagged ones can be blocked.
    const requiresCodeBlockedReason = computed(() => (props.draft.mode === 'existing'
      ? codeRequirementBlockedReason(selected.value, props.collectionOwnerId)
      : ''));

    function patch(changes: Partial<DraftSticker>) {
      emit('update:draft', { ...props.draft, ...changes });
    }

    function inputValue(event: Event) {
      return (event.target as HTMLInputElement).value;
    }

    async function searchStickers(query: string) {
      loadingStickers.value = true;
      try {
        const response = await makeGraphRequest(
          'query',
          findApprovedOrgStickers,
          { orgId: props.orgId, searchString: query || null },
          getAuthToken(),
        );
        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to load stickers');
          return;
        }
        stickers.value = response?.data?.findApprovedOrgStickers ?? [];
        if (!query) hasAnySticker.value = stickers.value.length > 0;
        loadedOnce.value = true;
      } finally {
        loadingStickers.value = false;
      }
    }

    function setMode(mode: DraftStickerMode) {
      if (mode === props.draft.mode) return;
      selected.value = null;
      patch({
        mode, name: '', imageUrl: '', thumbnailUrl: '', stickerId: '',
      });
      if (mode === 'existing' && !loadedOnce.value) searchStickers('');
    }

    function selectSticker(id: string) {
      const sticker = stickerOptions.value.find((s) => s.id === id) || null;
      selected.value = sticker;
      const blocked = Boolean(codeRequirementBlockedReason(sticker, props.collectionOwnerId));
      // A sticker that can't take codes is added with the requirement off and no codes.
      patch({
        stickerId: id || '',
        name: sticker?.title || '',
        ...(blocked ? { requiresCode: false, codes: [] } : {}),
      });
    }

    async function onImageSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      input.value = '';
      if (!file) return;
      if (!beforeUpload(file)) {
        ElMessage.error(uploadStatus.message || 'Invalid image');
        return;
      }
      uploading.value = true;
      try {
        await handleUploadRequest({ file });
        if (uploadStatus.status === 'ok') {
          patch({ imageUrl: uploadStatus.resourceUrl, thumbnailUrl: uploadStatus.thumbnailUrl });
        } else {
          ElMessage.error(uploadStatus.message || 'Upload failed');
        }
      } finally {
        uploading.value = false;
      }
    }

    function toggleClue() {
      if (showClue.value) patch({ clue: '' });
      showClue.value = !showClue.value;
    }

    const codeInputPlaceholder = computed(() => placeholderForFormat(props.codeFormat));
    // Custom-format collections have no generated codes, so a code must be typed.
    const canAddCode = computed(
      () => props.codeFormat !== 'CUSTOM' || Boolean(newCode.value.trim()),
    );

    function addCode() {
      if (!canAddCode.value) return;
      const code = newCode.value.trim().toUpperCase() || generateCollectionCode(props.codeFormat);
      if (props.draft.codes.some((c) => c.code === code)) {
        ElMessage.warning('That code is already on this sticker');
        return;
      }
      patch({ codes: [...props.draft.codes, { code, maxUses: props.defaultCodeMaxUses }] });
      newCode.value = '';
    }

    function removeCode(i: number) {
      patch({ codes: props.draft.codes.filter((_, idx) => idx !== i) });
    }

    function setCodeMaxUses(i: number, event: Event) {
      const maxUses = parseMaxUses(inputValue(event));
      patch({
        codes: props.draft.codes.map((c, idx) => (idx === i ? { ...c, maxUses } : c)),
      });
    }

    onMounted(() => {
      if (props.draft.mode === 'existing') searchStickers('');
    });

    return {
      expanded,
      hasAnySticker,
      showClue,
      newCode,
      uploading,
      loadingStickers,
      loadedOnce,
      stickerOptions,
      previewUrl,
      requiresCodeBlockedReason,
      patch,
      inputValue,
      setMode,
      searchStickers,
      selectSticker,
      onImageSelected,
      toggleClue,
      codeInputPlaceholder,
      canAddCode,
      addCode,
      removeCode,
      setCodeMaxUses,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../sticker-card';

// Element Plus select styled as the design's native <select>.
.sticker-select {
  width: 100%;

  :deep(.el-select__wrapper),
  :deep(.el-input__wrapper) {
    min-height: 42px;
    background: $gray-50;
    border-radius: 8px;
    box-shadow: 0 0 0 1px $gray-200 inset;
    padding: 0 12px;
    font-size: 14px;
  }

  :deep(.el-select__wrapper.is-focused),
  :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 1px $teal-light inset, 0 0 0 1px $teal-light;
  }

  :deep(.el-select__placeholder),
  :deep(.el-input__inner) {
    color: $gray-800;
  }

  :deep(.el-select__placeholder.is-transparent) {
    color: $gray-400;
  }
}
</style>
